import { Col, Row, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getI18nCountryByKey } from "shared/helpers/i18nCountry";
import { getI18nGenreByKey } from "shared/helpers/i18nGenre";
import { I18NColumn } from "../rankings/I18nColumn";
import moment from "moment";
import { getCapitalizedLang } from "shared/helpers/capitalized-lang";

const LabelInfoTables: React.FC<any> = (props) => {
  const {
    artists,
    countries,
    genresStrm,
    releases,
    tracks,
    history,
    rowSelection,
    selectedTrend,
  } = props;
  const { t, i18n } = useTranslation("radar");

  const genreByKeys = useSelector((state: any) => state.genres.genreByKeys);
  const countryBykeys = useSelector(
    (state: any) => state.countries.countryByKeys
  );

  const onClickArtist = (artist: any) =>
    artist?.artistIdStrm &&
    history.push(`/artist-profile/${artist?.artistIdStrm}`);

  const getCountryByKey = (key: string) =>
    countryBykeys[key][`description${getCapitalizedLang(i18n)}`];

  const getGenreByKey = (key: string) => genreByKeys[key][i18n.language];

  const artistColumns: ColumnsType<any> = [
    {
      title: t("pages.LabelProfile.artist"),
      dataIndex: "artistName",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.country"),
      dataIndex: "country",
      width: "15%",
      align: "center",
      render: (value) => {
        const countries =
          value !== "NaN"
            ? value?.filter((x: string) => x !== "NaN").sort()
            : [];
        return <>{getI18nCountryByKey(countries[0], i18n, countryBykeys)}</>;
      },
    },
    {
      title: t("pages.LabelProfile.genre"),
      dataIndex: "genresStrm",
      width: "15%",
      align: "center",
      render: (value) => {
        const genres =
          value !== "NaN"
            ? value?.filter((x: string) => x !== "NaN").sort()
            : [];
        return (
          <I18NColumn
            listRow={genres}
            label={t("pages.ArtistsRanking.genres")}
            i18nFunction={(genre: string) =>
              getI18nGenreByKey(genre, i18n, genreByKeys)
            }
            t={t}
          />
        );
      },
    },
    {
      title: t("pages.LabelProfile.popularity"),
      dataIndex: "popularityIndex",
      align: "center",
      width: "15%",
      render: (value: Number) => (value ? value.toFixed(0) : "-"),
    },
    {
      title: t("pages.LabelProfile.releases"),
      dataIndex: "releaseCnt",
      align: "center",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.tracks"),
      dataIndex: "trackCnt",
      align: "center",
      width: "15%",
    },
  ];

  const countriesColumns: ColumnsType<any> = [
    {
      title: t("pages.LabelProfile.country"),
      dataIndex: "country",
      width: "15%",
      render: (text: string) => getCountryByKey(text),
    },
    {
      title: t("pages.LabelsRanking.popularityIndex"),
      dataIndex: "popularityIndex",
      align: "center",
      width: "15%",
      render: (value: Number) => (value ? value.toFixed(1) : "-"),
    },
    {
      title: t("pages.LabelProfile.artists"),
      dataIndex: "artistCnt",
      align: "center",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.releases"),
      dataIndex: "releaseCnt",
      align: "center",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.tracks"),
      dataIndex: "trackCnt",
      align: "center",
      width: "15%",
    },
  ];

  const genresColumns: ColumnsType<any> = [
    {
      title: t("pages.LabelProfile.genre"),
      dataIndex: "genreStrm",
      width: "15%",
      render: (text: string) => getGenreByKey(text),
    },
    {
      title: t("pages.LabelsRanking.popularityIndex"),
      dataIndex: "popularityIndex",
      align: "center",
      width: "15%",
      render: (value: Number) => (value ? value.toFixed(1) : "-"),
    },
    {
      title: t("pages.LabelProfile.artists"),
      dataIndex: "artistCnt",
      align: "center",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.releases"),
      dataIndex: "releaseCnt",
      align: "center",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.tracks"),
      dataIndex: "trackCnt",
      align: "center",
      width: "15%",
    },
  ];

  const releasesColumns: ColumnsType<any> = [
    {
      title: t("pages.LabelProfile.release"),
      dataIndex: "releaseName",
      width: "15%",
    },
    {
      title: t("pages.LabelProfile.artists"),
      align: "center",
      width: "15%",
      render: (value: any) => {
        return value?.artists?.map((artist: any, index: any) => (
          <ArtistSpan
            artist={artist}
            index={index}
            onClickArtist={onClickArtist}
            value={value}
          />
        ));
      },
    },
    {
      title: t("pages.ArtistProfile.releaseDate"),
      dataIndex: "releaseDate",
      width: "20%",
      render: (value) => <>{moment(value).format("DD/MM/yyyy")}</>,
    },
    {
      title: t(`pages.ArtistProfile.type`),
      dataIndex: "releaseType",
      width: "10%",
    },
    {
      title: t("pages.LabelProfile.popularity"),
      dataIndex: "popularityIndex",
      align: "center",
      width: "15%",
      render: (value: Number) => (value ? value.toFixed(0) : "-"),
    },
  ];

  const tracksColumns: ColumnsType<any> = [
    {
      title: t("pages.LabelProfile.track"),
      dataIndex: "trackName",
      width: "30%",
    },
    {
      title: t("pages.LabelProfile.artists"),
      align: "center",
      width: "30%",
      render: (value: any) => {
        return value?.artists?.map((artist: any, index: any) => (
          <ArtistSpan
            artist={artist}
            index={index}
            onClickArtist={onClickArtist}
            value={value}
          />
        ));
      },
    },
    {
      title: t("pages.ArtistProfile.releaseDate"),
      dataIndex: "releaseDate",
      width: "20%",
      render: (value) => <>{moment(value).format("DD/MM/yyyy")}</>,
    },
    {
      title: t("pages.LabelProfile.popularity"),
      dataIndex: "popularityIndex",
      align: "center",
      width: "20%",
      render: (value: Number) => (value ? value.toFixed(0) : "-"),
    },
  ];

  const onRowClick = (data: any) => {
    if (data.artistIdStrm) history.push(`/artist-profile/${data.artistIdStrm}`);
  };

  const ReleasesTable = () => (
    <Col xs={24}>
      <span className="table-title">
        {t("pages.LabelProfile.mostRelevantReleases")}
      </span>{" "}
      <Table
        columns={releasesColumns}
        dataSource={releases}
        pagination={{ showQuickJumper: false, showPrevNextJumpers: false }}
      />
    </Col>
  );

  const TracksTable = () => (
    <Col xs={24}>
      <span className="table-title">
        {t("pages.LabelProfile.mostRelevantTracks")}
      </span>{" "}
      <Table
        columns={tracksColumns}
        dataSource={tracks}
        rowSelection={
          selectedTrend === "trackPopularityHistory" && rowSelection
        }
        rowKey={(row: any) => row.trackId}
        pagination={{
          defaultPageSize: 7,
          showQuickJumper: false,
          showPrevNextJumpers: false,
        }}
        key="artist-track-popularity-history-table"
      />
    </Col>
  );

  return (
    <Row gutter={[32, 32]} style={{ marginTop: 32 }}>
      {selectedTrend === "releaseTrend" ? (
        <>
          <ReleasesTable />
          <TracksTable />
        </>
      ) : (
        <>
          <TracksTable />
          <ReleasesTable />
        </>
      )}
      <Col xs={24}>
        <span className="table-title">
          {t("pages.LabelProfile.mostRelevantCountries")}
        </span>
        <Table
          columns={countriesColumns}
          dataSource={countries}
          pagination={{ showQuickJumper: false, showPrevNextJumpers: false }}
        />
      </Col>
      <Col xs={24}>
        <span className="table-title">
          {t("pages.LabelProfile.mostRelevantGenres")}
        </span>{" "}
        <Table
          columns={genresColumns}
          dataSource={genresStrm}
          pagination={{ showQuickJumper: false, showPrevNextJumpers: false }}
        />
      </Col>
      <Col xs={24}>
        <span className="table-title">
          {t("pages.LabelProfile.mostRelevantArtists")}
        </span>
        <Table
          columns={artistColumns}
          dataSource={artists}
          pagination={{ showQuickJumper: false, showPrevNextJumpers: false }}
          onRow={(data) => ({ onClick: () => onRowClick(data) })}
          rowClassName="cursor-pointer"
        />
      </Col>
    </Row>
  );
};

interface ArtistSpanProps {
  artist: any;
  index: number;
  onClickArtist: (artist: any) => void;
  value: any;
}

const ArtistSpan: React.FC<ArtistSpanProps> = ({
  artist,
  onClickArtist,
  value,
  index,
}) => (
  <span className="cursor-pointer" onClick={() => onClickArtist(artist)}>
    {`${artist?.artistName}${index + 1 !== value?.artists?.length ? ", " : ""}`}
  </span>
);

export default LabelInfoTables;
